import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { AuditTrail, SelectListOption, SelectListParams } from '../shared.types';

@Injectable({
    providedIn: 'root'
})
export class CrudService<T> {
    constructor(protected http: HttpClient, protected apiUrl: string) {}

    index(obj?: any): Observable<Array<T>> {
        if (obj) {
            const params = new URLSearchParams(obj).toString();
            return this.http.get<Array<T>>(`/api/${this.apiUrl}?${params}`);
        }
        return this.http.get<Array<T>>(`/api/${this.apiUrl}`);
    }

    listMap(obj: any): Observable<T> {
        return this.http.post<T>(`/api/${this.apiUrl}/list?`, obj);
    }

    list(obj?: any): Observable<Array<T>> {
        return this.http.post<Array<T>>(`/api/${this.apiUrl}/list?`, obj);
    }

    previewPdf(id: number, attachments: boolean) {
        return this.http.get(`/api/${this.apiUrl}/${id}/pdf?attachments=${attachments}`, {
            responseType: 'blob',
            observe: 'response'
        });
    }

    selectList(parameters?: SelectListParams): Observable<Array<SelectListOption>> {
        const paramsArray: Array<string> = [];
        if (parameters) {
            for (const p in parameters) {
                paramsArray.push(`${p}=${parameters[p]}`);
            }
        }
        const params = paramsArray.join('&');
        return this.http.get<Array<SelectListOption>>(`/api/${this.apiUrl}/selectList?${params}`);
    }

    selectListPost(parameters: SelectListParams): Observable<Array<SelectListOption>> {
        return this.http.post<Array<SelectListOption>>(`/api/${this.apiUrl}/selectList`, parameters);
    }

    show(id: number | string | bigint, obj?: any): Observable<T> {
        if (obj) {
            const params = new URLSearchParams(obj);
            return this.http.get<T>(`/api/${this.apiUrl}/${id}?${params}`);
        }
        return this.http.get<T>(`/api/${this.apiUrl}/${id}`);
    }

    save(element: T): Observable<T> {
        return this.http.post<T>(`/api/${this.apiUrl}`, element);
    }

    createManyFromOne(element: T): Observable<Array<T>> {
        return this.http.post<Array<T>>(`/api/${this.apiUrl}/create`, element);
    }

    adminSave(id: number, element: T): Observable<T> {
        return this.http.post<T>(`/api/${this.apiUrl}/${id}/adminSave`, element);
    }

    delete(id: number | bigint | string, urlParams?: URLSearchParams) {
        if (urlParams) {
            return this.http.delete(`/api/${this.apiUrl}/${id}?${urlParams}`);
        }
        return this.http.delete(`/api/${this.apiUrl}/${id}`);
    }

    resolveActors<T>(id: number, objects: T[], properties: string[]): Promise<T[]> {
        const actors: Array<string> = [];
        const seen: {} = {};
        properties.forEach((p) => {
            const theseActors = Array.from(new Set(objects.map((o) => o[p])));
            theseActors.forEach((a) => {
                if (!seen[a]) {
                    seen[a] = true;
                    actors.push(a);
                }
            });
        });
        return new Promise((resolve, reject) => {
            this.interpolateActors(id, actors).subscribe(
                (map) => {
                    objects.forEach((o) => {
                        properties.forEach((p) => {
                            const v = o[p];
                            if (map[v]) {
                                o[p] = map[v];
                            }
                        });
                    });
                    resolve(objects);
                },
                () => {
                    resolve(objects);
                }
            );
        });
    }

    auditTrail(id: number, className?: string): Observable<Array<AuditTrail>> {
        return this.http.post<Array<AuditTrail>>(`/api/auditTrail/list`, {
            className: className ? className : this.apiUrl,
            objectId: id
        });
    }

    //expected result is an object with netids as the keys and names as the value
    private interpolateActors(id: number, actors: Array<string>): Observable<any> {
        return this.http.post<any>(`/api/auditTrail/${id}/interpolateActors`, {
            actors: actors
        });
    }
}
